import type { TFunction } from 'i18next';
import { ErrorCode } from '@bliks-io/contracts';
import type { ApiError } from '@/lib/agent.types';
import { toInterpolationValues, translateErrorMessage } from '@/lib/errors';
import type { ParsedDepartment, ParsedUser } from './excelParsers';

// Exhaustive by construction: departments block only on processes, users on
// systems or processes (ADR 0011, third amendment), OrgFunctions on being
// some member's Hauptfunktion.
const REMOVAL_BLOCKED_KEYS: Record<string, string> = {
  'department.processes': 'initialSetup.removalBlocked.department.processes',
  'user.systems': 'initialSetup.removalBlocked.user.systems',
  'user.processes': 'initialSetup.removalBlocked.user.processes',
  'orgFunction.mainRole': 'initialSetup.removalBlocked.orgFunction.mainRole'
};

/**
 * Wizard-aware wrapper around the generic API error translation: a blocked
 * surgical removal gets a human banner naming the blocking entity kind and
 * count instead of the bare error code.
 */
export function translateWizardSubmitError(apiError: ApiError, t: TFunction): string {
  if (apiError.message === ErrorCode.INITIAL_SETUP_REMOVAL_BLOCKED && apiError.metadata) {
    const key = REMOVAL_BLOCKED_KEYS[`${apiError.metadata.kind}.${apiError.metadata.blockedBy}`];
    if (key) return t(key, { count: Number(apiError.metadata.count) });
  }
  return translateErrorMessage(apiError, t);
}

// Only the backend field names that differ from the client draft field names
// need a remap; every other field falls through unchanged below.
const BACKEND_TO_CLIENT_FIELD_MAP: Record<string, string> = {
  parentAbbreviation: 'parentAbbr'
};

// Metadata values naming offending rows come as a single value or as the full
// list of a bulk conflict; matching is case-insensitive because the backend
// reports lowercased values while rows keep their typed casing.
function matchesMetadataValue(rowValue: string, metadataValue: unknown): boolean {
  const values = Array.isArray(metadataValue) ? metadataValue : [metadataValue];
  return values.some((value) => String(value).toLowerCase() === rowValue.toLowerCase());
}

/**
 * Maps backend API errors to specific rows in departments or users lists,
 * marking them as invalid with translated error messages.
 */
export function mapApiErrorsToRows(
  apiError: ApiError,
  t: TFunction,
  departments: ParsedDepartment[],
  users: ParsedUser[]
): { updatedDepartments?: ParsedDepartment[]; updatedUsers?: ParsedUser[] } {
  if (!apiError.errors || !apiError.metadata) return {};

  const backendErrors = Object.entries(apiError.errors || {}).flatMap(([fieldName, errors]) => {
    const errorArray = Array.isArray(errors) ? errors : [errors];
    const clientFieldName = BACKEND_TO_CLIENT_FIELD_MAP[fieldName] || fieldName;
    return errorArray.map((err) => {
      const translatedMessage = t(err, {
        defaultValue: err,
        ...toInterpolationValues(apiError.metadata)
      });
      return { field: clientFieldName, message: translatedMessage };
    });
  });

  const result: { updatedDepartments?: ParsedDepartment[]; updatedUsers?: ParsedUser[] } = {};

  // Map to departments by abbreviation
  if (apiError.metadata.abbreviation) {
    result.updatedDepartments = departments.map((dept) => {
      if (matchesMetadataValue(dept.data.abbreviation, apiError.metadata?.abbreviation)) {
        return {
          ...dept,
          isValid: false,
          errors: [...(dept.errors || []), ...backendErrors]
        };
      }
      return dept;
    });
  }

  // Map to users by email
  if (apiError.metadata.email) {
    result.updatedUsers = users.map((user) => {
      if (matchesMetadataValue(user.data.email, apiError.metadata?.email)) {
        return {
          ...user,
          isValid: false,
          errors: [...(user.errors || []), ...backendErrors]
        };
      }
      return user;
    });
  }

  // Map to users by invalid department references. An error can carry both
  // `email` and `departments`, so accumulate onto the marks the email branch
  // already made instead of remapping the pristine list and clobbering them.
  if (apiError.metadata.departments && Array.isArray(apiError.metadata.departments)) {
    const invalidDepartments = apiError.metadata.departments;
    result.updatedUsers = (result.updatedUsers ?? users).map((user) => {
      if (invalidDepartments.includes(user.data.department)) {
        return {
          ...user,
          isValid: false,
          errors: [...(user.errors || []), ...backendErrors]
        };
      }
      return user;
    });
  }

  return result;
}
